const RECORD_VERSION = 1;
const RECORD_KEY_PREFIX = 'assets-upload:v1:';

const ERROR_MESSAGES = Object.freeze({
  UPLOAD_AUTH_REQUIRED: 'Your sign-in must be renewed before the upload can continue.',
  UPLOAD_CONFIGURATION_ERROR: 'The upload session is not valid for this file.',
  UPLOAD_FILE_MISMATCH: 'The selected file does not match the paused upload.',
  UPLOAD_FILE_UNREADABLE: 'The selected file could not be read.',
  UPLOAD_INCOMPLETE: 'The upload is paused because some chunks are still missing.',
  UPLOAD_PAUSED: 'The upload is paused.',
  UPLOAD_REJECTED: 'The upload service rejected this upload.',
  UPLOAD_SESSION_EXPIRED: 'The upload session has expired.',
  UPLOAD_STATUS_INVALID: 'The upload service returned an invalid status.',
  UPLOAD_STORAGE_ERROR: 'The upload session could not be saved in this browser.',
  UPLOAD_TEMPORARILY_UNAVAILABLE: 'The upload service is temporarily unavailable.',
});

const DEFAULT_RETRY_OPTIONS = Object.freeze({
  maxAttempts: 6,
  attemptTimeoutMs: 75_000,
  baseDelayMs: 1_000,
  maxDelayMs: 30_000,
  maxRetryAfterMs: 30_000,
});

/**
 * A stable, user-safe upload error. Raw upstream response bodies and exception
 * messages are deliberately not copied into `message`.
 */
export class UploadError extends Error {
  constructor(code, options = {}) {
    super(ERROR_MESSAGES[code] || ERROR_MESSAGES.UPLOAD_REJECTED);
    this.name = 'UploadError';
    this.code = code;
    this.retryable = Boolean(options.retryable);
    this.status = Number.isInteger(options.status) ? options.status : null;
    this.requestId = sanitizeRequestId(options.requestId);
    this.chunkIndex = Number.isInteger(options.chunkIndex) ? options.chunkIndex : null;
    if (options.cause !== undefined) {
      Object.defineProperty(this, 'cause', {
        configurable: true,
        value: options.cause,
        writable: true,
      });
    }
  }
}

function sanitizeRequestId(value) {
  if (typeof value !== 'string') return null;
  const sanitized = value.replace(/[^a-zA-Z0-9._:-]/g, '').slice(0, 128);
  return sanitized || null;
}

function uploadError(code, options = {}) {
  return new UploadError(code, options);
}

function pauseError(options = {}) {
  return uploadError('UPLOAD_PAUSED', { ...options, retryable: true });
}

function assertNotPaused(signal) {
  if (signal?.aborted) throw pauseError();
}

function getRequestId(response) {
  return response?.headers?.get?.('x-request-id') || response?.headers?.get?.('x-correlation-id') || null;
}

function getRecordKey(uploadId) {
  if (typeof uploadId !== 'string' || uploadId.length === 0) {
    throw uploadError('UPLOAD_CONFIGURATION_ERROR');
  }
  return `${RECORD_KEY_PREFIX}${uploadId}`;
}

function validateStorage(storage) {
  if (
    !storage ||
    typeof storage.getItem !== 'function' ||
    typeof storage.setItem !== 'function' ||
    typeof storage.removeItem !== 'function'
  ) {
    throw uploadError('UPLOAD_STORAGE_ERROR');
  }
}

function validateRecord(record) {
  const validState = ['uploading', 'paused', 'chunks-complete', 'finalizing', 'failed'].includes(record?.state);

  if (
    record?.version !== RECORD_VERSION ||
    typeof record.uploadId !== 'string' ||
    typeof record.videoId !== 'string' ||
    typeof record.assetsVideoId !== 'string' ||
    typeof record.sessionId !== 'string' ||
    typeof record.chunkUploadUrlBase !== 'string' ||
    typeof record.chunkStatusUrl !== 'string' ||
    !Number.isInteger(record.chunkSize) ||
    record.chunkSize <= 0 ||
    !Number.isInteger(record.totalChunks) ||
    record.totalChunks <= 0 ||
    !record.file ||
    typeof record.file.name !== 'string' ||
    !Number.isSafeInteger(record.file.size) ||
    record.file.size <= 0 ||
    typeof record.file.type !== 'string' ||
    !Number.isFinite(record.file.lastModified) ||
    !/^[a-f0-9]{64}$/.test(record.file.sampleSha256) ||
    !Number.isInteger(record.confirmedCount) ||
    record.confirmedCount < 0 ||
    record.confirmedCount > record.totalChunks ||
    !validState
  ) {
    throw uploadError('UPLOAD_CONFIGURATION_ERROR');
  }

  return record;
}

/** Save a compact upload record. The server remains authoritative for chunks. */
export function saveUploadRecord(storage, record) {
  validateStorage(storage);
  validateRecord(record);

  try {
    storage.setItem(getRecordKey(record.uploadId), JSON.stringify(record));
  } catch (cause) {
    throw uploadError('UPLOAD_STORAGE_ERROR', { cause });
  }

  return record;
}

/** Load a versioned record. Corrupt or stale records are ignored safely. */
export function loadUploadRecord(storage, uploadId) {
  validateStorage(storage);

  let serialized;
  try {
    serialized = storage.getItem(getRecordKey(uploadId));
  } catch (cause) {
    throw uploadError('UPLOAD_STORAGE_ERROR', { cause });
  }

  if (!serialized) return null;

  try {
    return validateRecord(JSON.parse(serialized));
  } catch (error) {
    if (error instanceof UploadError) return null;
    return null;
  }
}

export function removeUploadRecord(storage, uploadId) {
  validateStorage(storage);

  try {
    storage.removeItem(getRecordKey(uploadId));
  } catch (cause) {
    throw uploadError('UPLOAD_STORAGE_ERROR', { cause });
  }
}

function toHex(bytes) {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function appendUint32(target, offset, value) {
  const view = new DataView(target.buffer, target.byteOffset, target.byteLength);
  view.setUint32(offset, value, false);
}

function appendUint64(target, offset, value) {
  const view = new DataView(target.buffer, target.byteOffset, target.byteLength);
  view.setBigUint64(offset, BigInt(value), false);
}

/**
 * Build a low-cost identity for a very large File. It hashes framed samples
 * from the start, middle, and end plus the exact byte size; it never reads the
 * whole movie into memory.
 */
export async function createSampledFileIdentity(file, { sampleSize = 64 * 1024, cryptoImpl = globalThis.crypto } = {}) {
  if (
    !file ||
    !Number.isSafeInteger(file.size) ||
    file.size <= 0 ||
    typeof file.slice !== 'function' ||
    !Number.isInteger(sampleSize) ||
    sampleSize <= 0 ||
    !cryptoImpl?.subtle?.digest
  ) {
    throw uploadError('UPLOAD_CONFIGURATION_ERROR');
  }

  const lastOffset = Math.max(0, file.size - sampleSize);
  const middleOffset = Math.max(0, Math.floor((file.size - Math.min(sampleSize, file.size)) / 2));
  const offsets = [...new Set([0, middleOffset, lastOffset])].sort((left, right) => left - right);
  const samples = [];

  for (const offset of offsets) {
    const end = Math.min(file.size, offset + sampleSize);
    let bytes;
    try {
      bytes = new Uint8Array(await file.slice(offset, end).arrayBuffer());
    } catch (cause) {
      throw uploadError('UPLOAD_FILE_UNREADABLE', { cause });
    }
    samples.push({ offset, bytes });
  }

  const sizePrefix = new TextEncoder().encode(String(file.size));
  const framingBytes = 4 + sizePrefix.length + samples.length * 12;
  const contentBytes = samples.reduce((total, sample) => total + sample.bytes.byteLength, 0);
  const framed = new Uint8Array(framingBytes + contentBytes);
  let cursor = 0;

  appendUint32(framed, cursor, sizePrefix.length);
  cursor += 4;
  framed.set(sizePrefix, cursor);
  cursor += sizePrefix.length;

  for (const sample of samples) {
    appendUint64(framed, cursor, sample.offset);
    cursor += 8;
    appendUint32(framed, cursor, sample.bytes.byteLength);
    cursor += 4;
    framed.set(sample.bytes, cursor);
    cursor += sample.bytes.byteLength;
  }

  let digest;
  try {
    digest = await cryptoImpl.subtle.digest('SHA-256', framed);
  } catch (cause) {
    throw uploadError('UPLOAD_FILE_UNREADABLE', { cause });
  }
  return {
    name: typeof file.name === 'string' ? file.name : '',
    size: file.size,
    type: typeof file.type === 'string' ? file.type : '',
    lastModified: Number.isFinite(file.lastModified) ? file.lastModified : 0,
    sampleSha256: toHex(new Uint8Array(digest)),
  };
}

export function fileIdentityMatches(expected, actual) {
  return Boolean(
    expected &&
      actual &&
      expected.name === actual.name &&
      expected.size === actual.size &&
      expected.type === actual.type &&
      expected.lastModified === actual.lastModified &&
      expected.sampleSha256 === actual.sampleSha256,
  );
}

function normalizeSession(session, fileIdentity, kind, now) {
  const uploadId = session?.uploadId || session?.sessionId;
  const chunkStatusUrl =
    session?.chunkStatusUrl ||
    (typeof session?.chunkUploadUrlBase === 'string' ? `${session.chunkUploadUrlBase}/status` : null);

  const record = {
    version: RECORD_VERSION,
    uploadId,
    kind: kind === 'replace' ? 'replace' : 'create',
    videoId: session?.videoId,
    assetsVideoId: session?.assetsVideoId,
    sessionId: session?.sessionId,
    chunkUploadUrlBase: session?.chunkUploadUrlBase,
    chunkStatusUrl,
    chunkSize: session?.chunkSize,
    totalChunks: session?.totalChunks,
    file: fileIdentity,
    state: 'uploading',
    confirmedCount: 0,
    updatedAt: now(),
  };

  validateRecord(record);
  if (Math.ceil(fileIdentity.size / record.chunkSize) !== record.totalChunks) {
    throw uploadError('UPLOAD_CONFIGURATION_ERROR');
  }
  return record;
}

/** Compute file identity and persist the session before the first request. */
export async function persistNewUploadSession({
  storage,
  session,
  file,
  kind = 'create',
  now = Date.now,
  identityOptions,
}) {
  const fileIdentity = await createSampledFileIdentity(file, identityOptions);
  const record = normalizeSession(session, fileIdentity, kind, now);
  return saveUploadRecord(storage, record);
}

function normalizeRanges(ranges, totalChunks) {
  if (!Array.isArray(ranges)) {
    throw uploadError('UPLOAD_STATUS_INVALID');
  }

  const parsed = ranges.map((range) => {
    const start = Array.isArray(range) ? range[0] : range?.start;
    const end = Array.isArray(range)
      ? range[1]
      : (range?.end ?? (Number.isInteger(range?.endExclusive) ? range.endExclusive - 1 : undefined));

    if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end < start || end >= totalChunks) {
      throw uploadError('UPLOAD_STATUS_INVALID');
    }
    return { start, end };
  });

  parsed.sort((left, right) => left.start - right.start || left.end - right.end);
  const merged = [];
  for (const range of parsed) {
    const previous = merged.at(-1);
    if (previous && range.start <= previous.end + 1) {
      previous.end = Math.max(previous.end, range.end);
    } else {
      merged.push({ ...range });
    }
  }
  return merged;
}

function indicesToRanges(indices, totalChunks) {
  if (!Array.isArray(indices)) {
    throw uploadError('UPLOAD_STATUS_INVALID');
  }

  const sorted = [...new Set(indices)].sort((left, right) => left - right);
  if (sorted.some((index) => !Number.isInteger(index) || index < 0 || index >= totalChunks)) {
    throw uploadError('UPLOAD_STATUS_INVALID');
  }

  const ranges = [];
  for (const index of sorted) {
    const previous = ranges.at(-1);
    if (previous && index === previous.end + 1) previous.end = index;
    else ranges.push({ start: index, end: index });
  }
  return ranges;
}

function missingRangesFromUploaded(uploadedChunks, totalChunks) {
  if (!Array.isArray(uploadedChunks)) {
    throw uploadError('UPLOAD_STATUS_INVALID');
  }

  const uploaded = new Set(uploadedChunks);
  // Duplicate uploaded indices are harmless and are emitted by the current
  // Assets implementation, so validate values but do not reject duplicates.
  for (const index of uploaded) {
    if (!Number.isInteger(index) || index < 0 || index >= totalChunks) {
      throw uploadError('UPLOAD_STATUS_INVALID');
    }
  }

  const missing = [];
  let rangeStart = null;
  for (let index = 0; index < totalChunks; index++) {
    if (!uploaded.has(index) && rangeStart === null) rangeStart = index;
    if (uploaded.has(index) && rangeStart !== null) {
      missing.push({ start: rangeStart, end: index - 1 });
      rangeStart = null;
    }
  }
  if (rangeStart !== null) {
    missing.push({ start: rangeStart, end: totalChunks - 1 });
  }
  return missing;
}

function countRanges(ranges) {
  return ranges.reduce((count, range) => count + (range.end - range.start + 1), 0);
}

/** Normalize both current and compact status response shapes. */
export function normalizeUploadStatus(payload, expectedTotalChunks) {
  const serverCompleted = payload?.status === 'completed' && payload?.complete === true;
  if (
    payload?.restartRequired === true ||
    payload?.status === 'cancelled' ||
    (payload?.expired === true && !serverCompleted)
  ) {
    throw uploadError('UPLOAD_SESSION_EXPIRED');
  }

  const totalChunks = payload?.totalChunks ?? expectedTotalChunks;
  if (!Number.isInteger(totalChunks) || totalChunks <= 0 || totalChunks !== expectedTotalChunks) {
    throw uploadError('UPLOAD_STATUS_INVALID');
  }

  let missingRanges;
  if (payload?.missingRanges !== undefined) {
    missingRanges = normalizeRanges(payload.missingRanges, totalChunks);
  } else if (payload?.missingChunks !== undefined) {
    missingRanges = indicesToRanges(payload.missingChunks, totalChunks);
  } else if (payload?.uploadedChunks !== undefined) {
    missingRanges = missingRangesFromUploaded(payload.uploadedChunks, totalChunks);
  } else if (payload?.complete === true) {
    missingRanges = [];
  } else {
    throw uploadError('UPLOAD_STATUS_INVALID');
  }

  const missingCount = countRanges(missingRanges);
  const complete = missingCount === 0 && payload?.complete === true;
  if (missingCount === 0 && !complete) {
    throw uploadError('UPLOAD_STATUS_INVALID');
  }

  return {
    status: typeof payload?.status === 'string' ? payload.status : 'uploading',
    totalChunks,
    missingRanges,
    missingCount,
    confirmedCount: totalChunks - missingCount,
    complete,
  };
}

function isChunkMissing(status, chunkIndex) {
  return status.missingRanges.some((range) => chunkIndex >= range.start && chunkIndex <= range.end);
}

function* missingChunkIndexes(ranges) {
  for (const range of ranges) {
    for (let index = range.start; index <= range.end; index++) yield index;
  }
}

/** Map a thrown fetch failure or HTTP response to a bounded action. */
export function classifyUploadFailure(value, { timedOut = false } = {}) {
  if (timedOut) {
    return {
      action: 'retry',
      code: 'UPLOAD_TEMPORARILY_UNAVAILABLE',
      retryable: true,
      status: null,
    };
  }

  if (!(value instanceof Response) && !Number.isInteger(value?.status)) {
    return {
      action: 'retry',
      code: 'UPLOAD_TEMPORARILY_UNAVAILABLE',
      retryable: true,
      status: null,
    };
  }

  const status = value.status;
  if (status >= 200 && status < 300) {
    return { action: 'success', code: null, retryable: false, status };
  }
  if (status === 401) {
    return {
      action: 'refresh-auth',
      code: 'UPLOAD_AUTH_REQUIRED',
      retryable: true,
      status,
    };
  }
  if (status === 409) {
    return {
      action: 'reconcile',
      code: 'UPLOAD_TEMPORARILY_UNAVAILABLE',
      retryable: true,
      status,
    };
  }
  if (status === 408 || status === 425 || status === 429 || (status >= 500 && status <= 599)) {
    return {
      action: 'retry',
      code: 'UPLOAD_TEMPORARILY_UNAVAILABLE',
      retryable: true,
      status,
    };
  }
  if (status === 404 || status === 410) {
    return {
      action: 'fail',
      code: 'UPLOAD_SESSION_EXPIRED',
      retryable: false,
      status,
    };
  }
  return {
    action: 'fail',
    code: 'UPLOAD_REJECTED',
    retryable: false,
    status,
  };
}

export function parseRetryAfter(value, nowMs = Date.now()) {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1_000;

  const dateMs = Date.parse(value);
  if (!Number.isFinite(dateMs)) return null;
  return Math.max(0, dateMs - nowMs);
}

export function calculateRetryDelay({
  attempt,
  retryAfter,
  random = Math.random,
  now = Date.now,
  baseDelayMs = DEFAULT_RETRY_OPTIONS.baseDelayMs,
  maxDelayMs = DEFAULT_RETRY_OPTIONS.maxDelayMs,
  maxRetryAfterMs = DEFAULT_RETRY_OPTIONS.maxRetryAfterMs,
}) {
  const exponent = Math.max(0, attempt - 1);
  const ceiling = Math.min(maxDelayMs, baseDelayMs * 2 ** exponent);
  const candidate = random();
  const randomValue = Number.isFinite(candidate) ? Math.max(0, Math.min(0.999_999_999, candidate)) : 0;
  const jitter = Math.floor(randomValue * ceiling);
  const retryAfterMs = parseRetryAfter(retryAfter, now());
  if (retryAfterMs === null) return jitter;
  return Math.min(maxRetryAfterMs, Math.max(jitter, retryAfterMs));
}

function defaultSleep(delayMs, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(pauseError());
      return;
    }

    const finish = () => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    };
    const timeout = setTimeout(finish, delayMs);
    const onAbort = () => {
      clearTimeout(timeout);
      reject(pauseError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function mergeRetryOptions(options) {
  const merged = { ...DEFAULT_RETRY_OPTIONS, ...options };
  if (
    !Number.isInteger(merged.maxAttempts) ||
    merged.maxAttempts <= 0 ||
    !Number.isFinite(merged.attemptTimeoutMs) ||
    merged.attemptTimeoutMs <= 0 ||
    !Number.isFinite(merged.baseDelayMs) ||
    merged.baseDelayMs < 0 ||
    !Number.isFinite(merged.maxDelayMs) ||
    merged.maxDelayMs < 0 ||
    !Number.isFinite(merged.maxRetryAfterMs) ||
    merged.maxRetryAfterMs < 0
  ) {
    throw uploadError('UPLOAD_CONFIGURATION_ERROR');
  }
  return merged;
}

async function fetchWithTimeout(
  fetchImpl,
  url,
  init,
  { signal, timeoutMs, setTimeoutImpl = setTimeout, clearTimeoutImpl = clearTimeout },
) {
  assertNotPaused(signal);
  const controller = new AbortController();
  let timedOut = false;
  const onPause = () => controller.abort(signal?.reason);
  signal?.addEventListener('abort', onPause, { once: true });
  const timeout = setTimeoutImpl(() => {
    timedOut = true;
    controller.abort('timeout');
  }, timeoutMs);

  try {
    const response = await fetchImpl(url, {
      ...init,
      signal: controller.signal,
    });
    return { response, timedOut: false };
  } catch (cause) {
    if (signal?.aborted) throw pauseError({ cause });
    return { error: cause, timedOut };
  } finally {
    clearTimeoutImpl(timeout);
    signal?.removeEventListener('abort', onPause);
  }
}

function joinUrl(baseUrl, path) {
  if (/^https?:\/\//i.test(path)) return path;
  const base = typeof baseUrl === 'string' ? baseUrl.replace(/\/$/, '') : '';
  if (!base) return path;
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

async function authHeaders(getAuthHeaders) {
  if (typeof getAuthHeaders !== 'function') return {};
  const headers = await getAuthHeaders();
  return headers && typeof headers === 'object' ? headers : {};
}

async function refreshAuthentication(authState, forceAuthRefresh) {
  if (authState.refreshed || typeof forceAuthRefresh !== 'function') {
    throw uploadError('UPLOAD_AUTH_REQUIRED', { retryable: true, status: 401 });
  }
  authState.refreshed = true;
  try {
    await forceAuthRefresh();
  } catch (cause) {
    throw uploadError('UPLOAD_AUTH_REQUIRED', {
      retryable: true,
      status: 401,
      cause,
    });
  }
}

async function waitForRetry(response, attempt, dependencies, retryOptions, signal) {
  const delay = calculateRetryDelay({
    attempt,
    retryAfter: response?.headers?.get?.('retry-after') || null,
    random: dependencies.random,
    now: dependencies.now,
    ...retryOptions,
  });
  await dependencies.sleep(delay, signal);
  assertNotPaused(signal);
}

async function requestStatus({
  record,
  apiBaseUrl,
  dependencies,
  retryOptions,
  authState,
  signal,
  maxAttempts = retryOptions.maxAttempts,
}) {
  const url = joinUrl(apiBaseUrl, record.chunkStatusUrl);
  let lastClassification = null;
  let lastRequestId = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    assertNotPaused(signal);
    const result = await fetchWithTimeout(
      dependencies.fetch,
      url,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...(await authHeaders(dependencies.getAuthHeaders)),
        },
      },
      {
        signal,
        timeoutMs: retryOptions.attemptTimeoutMs,
        setTimeoutImpl: dependencies.setTimeout,
        clearTimeoutImpl: dependencies.clearTimeout,
      },
    );
    const value = result.response || result.error;
    const classification = classifyUploadFailure(value, {
      timedOut: result.timedOut,
    });
    lastClassification = classification;
    lastRequestId = getRequestId(result.response);

    if (classification.action === 'success') {
      let payload;
      try {
        payload = await result.response.json();
      } catch (cause) {
        throw uploadError('UPLOAD_STATUS_INVALID', {
          status: result.response.status,
          requestId: lastRequestId,
          cause,
        });
      }
      if (
        (payload?.sessionId !== undefined && payload.sessionId !== record.sessionId) ||
        (payload?.videoId !== undefined && payload.videoId !== record.assetsVideoId)
      ) {
        throw uploadError('UPLOAD_STATUS_INVALID', {
          status: result.response.status,
          requestId: lastRequestId,
        });
      }
      return normalizeUploadStatus(payload, record.totalChunks);
    }

    if (classification.action === 'refresh-auth') {
      await refreshAuthentication(authState, dependencies.forceAuthRefresh);
      attempt--;
      continue;
    }
    if (classification.action === 'fail') {
      throw uploadError(classification.code, {
        status: classification.status,
        retryable: classification.retryable,
        requestId: lastRequestId,
      });
    }
    if (attempt < maxAttempts) {
      await waitForRetry(result.response, attempt, dependencies, retryOptions, signal);
    }
  }

  throw uploadError(lastClassification?.code || 'UPLOAD_TEMPORARILY_UNAVAILABLE', {
    status: lastClassification?.status,
    retryable: true,
    requestId: lastRequestId,
  });
}

async function tryReconcileChunk(options, chunkIndex) {
  try {
    const status = await requestStatus({ ...options, maxAttempts: 1 });
    return { status, confirmed: !isChunkMissing(status, chunkIndex) };
  } catch (error) {
    if (
      error instanceof UploadError &&
      ['UPLOAD_PAUSED', 'UPLOAD_AUTH_REQUIRED', 'UPLOAD_SESSION_EXPIRED'].includes(error.code)
    ) {
      throw error;
    }
    return null;
  }
}

async function putChunk({ record, file, chunkIndex, apiBaseUrl, dependencies, retryOptions, authState, signal }) {
  const start = chunkIndex * record.chunkSize;
  const end = Math.min(start + record.chunkSize, file.size);
  const chunk = file.slice(start, end);
  const url = joinUrl(apiBaseUrl, `${record.chunkUploadUrlBase}/${chunkIndex}`);
  let lastClassification = null;
  let lastRequestId = null;

  for (let attempt = 1; attempt <= retryOptions.maxAttempts; attempt++) {
    assertNotPaused(signal);
    const result = await fetchWithTimeout(
      dependencies.fetch,
      url,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/octet-stream',
          ...(await authHeaders(dependencies.getAuthHeaders)),
        },
        body: chunk,
      },
      {
        signal,
        timeoutMs: retryOptions.attemptTimeoutMs,
        setTimeoutImpl: dependencies.setTimeout,
        clearTimeoutImpl: dependencies.clearTimeout,
      },
    );
    const value = result.response || result.error;
    const classification = classifyUploadFailure(value, {
      timedOut: result.timedOut,
    });
    lastClassification = classification;
    lastRequestId = getRequestId(result.response);

    if (classification.action === 'success') return { status: null };

    if (classification.action === 'refresh-auth') {
      await refreshAuthentication(authState, dependencies.forceAuthRefresh);
      attempt--;
      continue;
    }
    if (classification.action === 'fail') {
      throw uploadError(classification.code, {
        status: classification.status,
        retryable: classification.retryable,
        requestId: lastRequestId,
        chunkIndex,
      });
    }

    const reconciliation = await tryReconcileChunk(
      {
        record,
        apiBaseUrl,
        dependencies,
        retryOptions,
        authState,
        signal,
      },
      chunkIndex,
    );
    if (reconciliation?.confirmed) return { status: reconciliation.status };

    if (attempt < retryOptions.maxAttempts) {
      await waitForRetry(result.response, attempt, dependencies, retryOptions, signal);
    }
  }

  throw uploadError(lastClassification?.code || 'UPLOAD_TEMPORARILY_UNAVAILABLE', {
    status: lastClassification?.status,
    retryable: true,
    requestId: lastRequestId,
    chunkIndex,
  });
}

function emitProgress(onProgress, confirmedCount, totalChunks, reconciled) {
  if (typeof onProgress !== 'function') return;
  const calculated = Math.floor((confirmedCount / totalChunks) * 100);
  const percent = reconciled && confirmedCount === totalChunks ? 100 : Math.min(99, calculated);
  onProgress({ confirmedCount, totalChunks, percent, reconciled });
}

function markRecord(record, storage, now, updates) {
  Object.assign(record, updates, { updatedAt: now() });
  saveUploadRecord(storage, record);
}

function normalizeDependencies(dependencies) {
  if (typeof dependencies?.fetch !== 'function') {
    throw uploadError('UPLOAD_CONFIGURATION_ERROR');
  }
  return {
    fetch: dependencies.fetch,
    getAuthHeaders: dependencies.getAuthHeaders,
    forceAuthRefresh: dependencies.forceAuthRefresh,
    sleep: dependencies.sleep || defaultSleep,
    random: dependencies.random || Math.random,
    now: dependencies.now || Date.now,
    setTimeout: dependencies.setTimeout || setTimeout,
    clearTimeout: dependencies.clearTimeout || clearTimeout,
  };
}

/**
 * Sequentially upload only server-reported missing chunks. Pass either a fresh
 * `session`, which is persisted before the first request, or an existing
 * persisted `record` to resume it.
 */
export async function uploadAssetsFile({
  file,
  session,
  record: suppliedRecord,
  storage,
  kind = 'create',
  apiBaseUrl = '',
  signal,
  onProgress,
  dependencies,
  retry: retryOverrides,
  identityOptions,
  persistEveryChunks = 25,
  maxReconciliationRounds = 3,
}) {
  validateStorage(storage);
  const deps = normalizeDependencies(dependencies);
  const retryOptions = mergeRetryOptions(retryOverrides);
  const authState = { refreshed: false };
  let record = suppliedRecord;

  if (record) {
    validateRecord(record);
    const actualIdentity = await createSampledFileIdentity(file, identityOptions);
    if (!fileIdentityMatches(record.file, actualIdentity)) {
      throw uploadError('UPLOAD_FILE_MISMATCH');
    }
    saveUploadRecord(storage, record);
  } else {
    record = await persistNewUploadSession({
      storage,
      session,
      file,
      kind,
      now: deps.now,
      identityOptions,
    });
  }

  if (
    !Number.isInteger(persistEveryChunks) ||
    persistEveryChunks <= 0 ||
    !Number.isInteger(maxReconciliationRounds) ||
    maxReconciliationRounds <= 0
  ) {
    throw uploadError('UPLOAD_CONFIGURATION_ERROR');
  }

  try {
    assertNotPaused(signal);
    markRecord(record, storage, deps.now, {
      state: 'uploading',
      lastErrorCode: null,
    });

    let status = await requestStatus({
      record,
      apiBaseUrl,
      dependencies: deps,
      retryOptions,
      authState,
      signal,
    });
    record.confirmedCount = status.confirmedCount;
    saveUploadRecord(storage, record);
    emitProgress(onProgress, status.confirmedCount, status.totalChunks, status.complete);

    for (let round = 0; !status.complete && round < maxReconciliationRounds; round++) {
      let confirmedCount = status.confirmedCount;
      let chunksSincePersist = 0;

      for (const chunkIndex of missingChunkIndexes(status.missingRanges)) {
        const result = await putChunk({
          record,
          file,
          chunkIndex,
          apiBaseUrl,
          dependencies: deps,
          retryOptions,
          authState,
          signal,
        });

        if (result.status) confirmedCount = result.status.confirmedCount;
        else confirmedCount = Math.min(record.totalChunks, confirmedCount + 1);
        record.confirmedCount = confirmedCount;
        chunksSincePersist++;

        if (chunksSincePersist >= persistEveryChunks) {
          saveUploadRecord(storage, {
            ...record,
            updatedAt: deps.now(),
          });
          chunksSincePersist = 0;
        }
        emitProgress(onProgress, confirmedCount, record.totalChunks, false);
      }

      status = await requestStatus({
        record,
        apiBaseUrl,
        dependencies: deps,
        retryOptions,
        authState,
        signal,
      });
      record.confirmedCount = status.confirmedCount;
      saveUploadRecord(storage, { ...record, updatedAt: deps.now() });
      emitProgress(onProgress, status.confirmedCount, status.totalChunks, status.complete);
    }

    if (!status.complete) {
      throw uploadError('UPLOAD_INCOMPLETE', { retryable: true });
    }

    markRecord(record, storage, deps.now, {
      state: 'chunks-complete',
      confirmedCount: record.totalChunks,
      lastErrorCode: null,
    });
    return { record, status };
  } catch (error) {
    const safeError =
      error instanceof UploadError
        ? error
        : uploadError('UPLOAD_TEMPORARILY_UNAVAILABLE', {
            retryable: true,
            cause: error,
          });
    const recoverable = safeError.retryable || safeError.code === 'UPLOAD_PAUSED';
    markRecord(record, storage, deps.now, {
      state: recoverable ? 'paused' : 'failed',
      lastErrorCode: safeError.code,
    });
    throw safeError;
  }
}

/** A small convenience wrapper for UI pause buttons. */
export function createUploadController() {
  const controller = new AbortController();
  return {
    signal: controller.signal,
    pause() {
      if (!controller.signal.aborted) controller.abort('user-paused');
    },
  };
}

export const uploadRecordVersion = RECORD_VERSION;
